import clsx from 'clsx'
import type { MouseEventHandler, PropsWithChildren } from 'react'
import React, { useEffect, useState } from 'react'
import Alert from '../Alert/Alert'
import { AriaLiveRole } from '../utilities/aria'

interface SnackbarProps {
  anchorOrigin: AnchorOrigin
  autoHideDuration?: number
  open?: boolean
  role?: AriaLiveRole.Alert | AriaLiveRole.Status
  severity?: Severity
}

type Severity = 'error' | 'info' | 'success' | 'warning'

type HorizontalAchorOrigin = 'center' | 'left' | 'right'

type VerticalAnchorOrigin = 'bottom' | 'center' | 'top'

type AnchorOrigin = {
  horizontal: HorizontalAchorOrigin
  vertical: VerticalAnchorOrigin
}

function Snackbar({
  anchorOrigin: { horizontal, vertical },
  autoHideDuration,
  children,
  open = true,
  role = AriaLiveRole.Alert,
  severity = 'success',
}: PropsWithChildren<SnackbarProps>): JSX.Element | null {
  const [visible, setVisible] = useState(() => open)

  useEffect(() => {
    if (autoHideDuration !== undefined) {
      setTimeout(() => setVisible(false), autoHideDuration)
    }
  }, [autoHideDuration])

  useEffect(() => {
    setVisible(open)
  }, [open])

  return visible ? (
    <div
      className={clsx(
        'fixed flex shadow-6 rounded-md w-full max-w-xs z-9999',
        horizontal === 'left'
          ? 'left-0 ml-4 md:ml-8'
          : horizontal === 'right'
          ? 'right-0 mr-4 md:mr-8'
          : 'left-half',
        vertical === 'bottom'
          ? 'bottom-0 mb-4 md:mb-8'
          : vertical === 'top'
          ? 'top-0 mt-4 md:mt-8'
          : 'top-half',
      )}
    >
      <Alert role={role} severity={severity}>
        {children}
      </Alert>
    </div>
  ) : null
}

function Content({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return <Alert.Content>{children}</Alert.Content>
}

function Description({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return <Alert.Description>{children}</Alert.Description>
}

function Title({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return <Alert.Title>{children}</Alert.Title>
}

function Actions({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return <Alert.Actions>{children}</Alert.Actions>
}

interface ButtonProps {
  dataSet?: Record<string, string | undefined>
  onClick?: MouseEventHandler<HTMLButtonElement>
}

function Button({
  children,
  dataSet,
  onClick,
}: PropsWithChildren<ButtonProps>): JSX.Element {
  return (
    <Alert.Button dataSet={dataSet} onClick={onClick}>
      {children}
    </Alert.Button>
  )
}

Snackbar.Actions = Actions
Snackbar.Button = Button
Snackbar.Content = Content
Snackbar.Description = Description
Snackbar.Title = Title

export default Snackbar
